import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '@/config/firebase';
import { apiClient } from '@/core/api/client';
import type { ApiResponse } from '@/core/api/types';

/**
 * Calls the Firebase custom-token exchange endpoint.
 *
 * The axios interceptor (wired in client.ts) attaches the existing JWT as
 * Bearer so the .NET API can mint a scoped Firebase custom token from it.
 *
 * Returns null (and logs a warning) on 404 or any other failure so that
 * a missing or not-yet-deployed endpoint never blocks the React-side login.
 */
export async function exchangeForFirebaseToken(): Promise<string | null> {
  try {
    const res = await apiClient.post<ApiResponse<{ customToken: string }>>(
      '/api/v1/Login/firebase-token'
    );
    return res.data.data.customToken;
  } catch (err) {
    // TODO(ui-generator): remove the cast once the endpoint is confirmed deployed;
    // until then we treat any failure as "not available" to keep login non-blocking.
    const status = (err as { status?: number }).status ?? 0;
    if (status === 404) {
      console.warn('[auth] Firebase token exchange endpoint not found (404) — skipping Firebase auth.');
    } else {
      console.warn('[auth] Firebase token exchange failed — skipping Firebase auth.', err);
    }
    return null;
  }
}

/**
 * Signs into Firebase with the custom token minted by the .NET API.
 * No-ops with a warning if the Firebase Auth instance is null (e.g. no
 * VITE_FIREBASE_* env vars set, as in CI or a bare dev clone).
 */
export async function signInWithFirebaseToken(customToken: string): Promise<void> {
  if (!auth) {
    console.warn('[auth] Firebase Auth is not initialised — skipping signInWithCustomToken.');
    return;
  }
  await signInWithCustomToken(auth, customToken);
}
